import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { ArrowRight, ChevronDown, Menu, X, LayoutDashboard, LogOut, User, Phone } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { getAdminNav } from "@/config/adminModules";
import CallbackPopup from "@/components/CallbackPopup";

const LOGO_URL = "/img/logo-energy-market.png";

// Menu commercial (visiteurs / users)
const navigation = [
    { to: "/comparateur", label: "Comparateur" },
    {
        label: "Fournisseurs",
        children: [
            { to: "/fournisseurs", label: "Tous les fournisseurs" },
            { to: "/fournisseurs/comparer", label: "Comparer" },
        ],
    },
    { to: "/pro/cee-solaire", label: "CEE & Solaire" },
    { to: "/isolation-pac", label: "Isolation & PAC à 1€" },
    { to: "/contact", label: "Contact" },
];

const CTA = { to: "/devis", label: "Demander un devis" };

export default function Header() {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout, hasPerm } = useAuth();

    // Menu admin — items à plat (ops/comms/analytics) + dropdown "Réglages"
    // (notifications + compte), filtrés par permission RBAC. Voir getAdminNav()
    // dans adminModules.js.
    const adminItems = useMemo(() => getAdminNav(hasPerm), [hasPerm]);

    const [mobileOpen, setMobileOpen] = useState(false);
    const [openDesktop, setOpenDesktop] = useState(null);
    const [isScrolledDown, setIsScrolledDown] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [callbackOpen, setCallbackOpen] = useState(false);

    // En mode compact = scrollé ET pas hover. Hover ré-expand le menu.
    const compactMode = isScrolledDown && !isHovered;

    useEffect(() => {
        let lastScrollY = window.scrollY;
        let ticking = false;
        const onScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    const y = window.scrollY;
                    setIsScrolledDown(y > lastScrollY && y > 100);
                    lastScrollY = y;
                    ticking = false;
                });
                ticking = true;
            }
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setMobileOpen(false);
        setOpenDesktop(null);
    }, [location.pathname]);

    useEffect(() => {
        document.documentElement.style.overflow = mobileOpen ? "hidden" : "";
        return () => {
            document.documentElement.style.overflow = "";
        };
    }, [mobileOpen]);

    const handleLogout = () => {
        logout();
        setMobileOpen(false);
        navigate("/");
    };

    const isPathActive = (to) =>
        to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

    // Menu adaptatif : admin voit ses items uniquement, le reste voit le menu commercial
    const isAdmin = user?.role === "admin";
    const navItems = isAdmin ? adminItems : navigation;

    return (
        <>
            <header
                data-testid="site-header"
                className={`fixed top-4 z-50 pointer-events-none transition-all duration-500 ease-in-out ${
                    isScrolledDown
                        ? "left-4 translate-x-0"
                        : "left-1/2 -translate-x-1/2"
                }`}
            >
                {/* Wrapper externe : pilule menu + CTA détaché à droite */}
                <div className="flex items-center gap-3">

                <div
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    className={`flex items-center flex-shrink-0 rounded-full bg-white/80 supports-[backdrop-filter]:bg-white/60 backdrop-blur-md shadow-md shadow-ink/5 ring-1 ring-line/50 pointer-events-auto transition-all duration-500 ease-in-out ${
                        compactMode ? "p-1 md:p-1.5" : "p-3 md:py-3 md:px-4"
                    }`}
                >
                    {/* Logo — shrink-0 pour ne pas être écrasé par un flex large */}
                    <Link
                        to="/"
                        data-testid="logo-link"
                        aria-label="Energy Market — accueil"
                        className="pl-2 pr-1 inline-flex items-center shrink-0"
                    >
                        <img
                            src={LOGO_URL}
                            alt="Energy Market"
                            className={`w-auto shrink-0 transition-all duration-500 ${
                                compactMode ? "h-14 md:h-16" : "h-11 md:h-12"
                            }`}
                        />
                    </Link>

                    {/* Bloc droite : nav + CTA, se rétracte au scroll */}
                    <div
                        className={`flex items-center flex-nowrap transition-all duration-500 ease-in-out ${
                            compactMode
                                ? "max-w-0 opacity-0 gap-0 overflow-hidden"
                                : "opacity-100 gap-4 md:gap-6 ml-2 overflow-visible"
                        }`}
                    >
                        {/* Nav desktop */}
                        <nav
                            aria-label="Navigation principale"
                            className="hidden lg:flex items-center gap-1 flex-nowrap whitespace-nowrap"
                        >
                            {navItems.map((item) => {
                                if (item.children) {
                                    const groupActive = item.children.some((c) =>
                                        isPathActive(c.to),
                                    );
                                    const open = openDesktop === item.label;
                                    return (
                                        <div
                                            key={item.label}
                                            className="relative"
                                            onMouseEnter={() => setOpenDesktop(item.label)}
                                            onMouseLeave={() => setOpenDesktop(null)}
                                        >
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setOpenDesktop(open ? null : item.label)
                                                }
                                                className={`inline-flex items-center gap-1 rounded-full px-4 py-2 text-sm font-medium transition-all ${
                                                    groupActive
                                                        ? "bg-ink text-cream"
                                                        : "text-charcoal hover:bg-cream hover:text-ink"
                                                }`}
                                                aria-expanded={open}
                                            >
                                                {item.label}
                                                <ChevronDown
                                                    size={14}
                                                    className={`transition-transform ${
                                                        open ? "rotate-180" : ""
                                                    }`}
                                                />
                                            </button>
                                            {open && (
                                                <div className="absolute left-0 top-full pt-2">
                                                    <div className="w-64 rounded-lg bg-white p-2 shadow-lg ring-1 ring-line">
                                                        {item.children.map((c) => (
                                                            <Link
                                                                key={c.to}
                                                                to={c.to}
                                                                className={`block rounded-md px-3 py-2 text-sm transition-colors ${
                                                                    location.pathname === c.to
                                                                        ? "bg-energy-soft text-ink"
                                                                        : "text-charcoal hover:bg-cream"
                                                                }`}
                                                            >
                                                                {c.label}
                                                            </Link>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                }
                                return (
                                    <NavLink
                                        key={item.to}
                                        to={item.to}
                                        end={item.to === "/"}
                                        className={({ isActive }) =>
                                            `rounded-full px-4 py-2 text-sm font-medium transition-all ${
                                                isActive
                                                    ? "bg-ink text-cream"
                                                    : "text-charcoal hover:bg-cream hover:text-ink"
                                            }`
                                        }
                                    >
                                        {item.label}
                                    </NavLink>
                                );
                            })}
                        </nav>

                        {/* CTA + user controls desktop */}
                        <div className="hidden lg:flex items-center gap-2">
                            {user ? (
                                <>
                                    {user.role !== "admin" && (
                                        <Link
                                            to="/dashboard"
                                            data-testid="header-dashboard-btn"
                                            className="inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-medium text-charcoal hover:bg-cream hover:text-ink transition-colors"
                                        >
                                            <LayoutDashboard size={14} />
                                            Espace client
                                        </Link>
                                    )}
                                    <button
                                        type="button"
                                        onClick={handleLogout}
                                        data-testid="header-logout-btn"
                                        className="inline-flex items-center gap-1 rounded-full px-3 py-2 text-sm font-medium text-charcoal/70 hover:bg-cream hover:text-ink transition-colors"
                                        aria-label="Déconnexion"
                                    >
                                        <LogOut size={14} />
                                    </button>
                                </>
                            ) : (
                                <Link
                                    to="/connexion"
                                    data-testid="header-login-btn"
                                    className="inline-flex items-center gap-1 rounded-full px-3 py-2 text-sm font-medium text-charcoal hover:bg-cream hover:text-ink transition-colors"
                                >
                                    <User size={14} />
                                    Connexion
                                </Link>
                            )}
                            {!isAdmin && (
                                <button
                                    type="button"
                                    onClick={() => setCallbackOpen(true)}
                                    data-testid="header-callback"
                                    className="inline-flex whitespace-nowrap items-center gap-1.5 rounded-full border-2 border-ink/15 px-4 py-2 text-sm font-medium text-ink hover:border-ink/40 hover:bg-cream transition-all"
                                >
                                    <Phone size={14} />
                                    Être rappelé
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Mobile burger */}
                    <button
                        type="button"
                        onClick={() => setMobileOpen(true)}
                        aria-label="Ouvrir le menu"
                        aria-expanded={mobileOpen}
                        className={`lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full text-ink transition-all duration-500 hover:bg-cream ${
                            compactMode ? "hidden" : "ml-4"
                        }`}
                    >
                        <Menu size={20} />
                    </button>
                </div>

                {/* CTA détaché à droite de la pilule (desktop, masqué en compact) */}
                {!isAdmin && !compactMode && (
                    <Link
                        to={CTA.to}
                        data-testid="header-cta"
                        className="hidden lg:inline-flex flex-shrink-0 whitespace-nowrap items-center justify-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-medium text-cream shadow-md shadow-ink/10 pointer-events-auto transition-colors hover:bg-ink-deep"
                    >
                        {CTA.label}
                        <ArrowRight size={14} />
                    </Link>
                )}

                </div>
            </header>

            {/* Drawer mobile */}
            {mobileOpen && (
                <div className="lg:hidden fixed inset-0 z-50 bg-cream/95 backdrop-blur-md">
                    <div className="flex justify-end p-6">
                        <button
                            type="button"
                            onClick={() => setMobileOpen(false)}
                            aria-label="Fermer le menu"
                            className="inline-flex h-10 w-10 items-center justify-center rounded-md text-ink ring-1 ring-line"
                        >
                            <X size={20} />
                        </button>
                    </div>
                    <nav
                        aria-label="Navigation mobile"
                        className="mx-auto max-w-2xl px-6 mt-4 pb-12"
                    >
                        <ul className="space-y-1">
                            {navItems.map((item) => {
                                if (item.children) {
                                    return (
                                        <li key={item.label}>
                                            <div className="px-3 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-charcoal/60">
                                                {item.label}
                                            </div>
                                            <ul>
                                                {item.children.map((c) => (
                                                    <li key={c.to}>
                                                        <Link
                                                            to={c.to}
                                                            className={`block rounded-md px-3 py-2.5 text-base ${
                                                                location.pathname === c.to
                                                                    ? "bg-energy-soft text-ink font-medium"
                                                                    : "text-charcoal"
                                                            }`}
                                                        >
                                                            {c.label}
                                                        </Link>
                                                    </li>
                                                ))}
                                            </ul>
                                        </li>
                                    );
                                }
                                const active = isPathActive(item.to);
                                return (
                                    <li key={item.to}>
                                        <Link
                                            to={item.to}
                                            className={`block rounded-md px-3 py-2.5 text-base ${
                                                active
                                                    ? "bg-energy-soft text-ink font-medium"
                                                    : "text-charcoal"
                                            }`}
                                        >
                                            {item.label}
                                        </Link>
                                    </li>
                                );
                            })}
                        </ul>

                        <div className="mt-6 space-y-2 border-t border-line pt-6">
                            {user ? (
                                <>
                                    {user.role !== "admin" && (
                                        <Link
                                            to="/dashboard"
                                            className="block rounded-md px-3 py-2.5 text-base text-charcoal"
                                        >
                                            Espace client
                                        </Link>
                                    )}
                                    <button
                                        type="button"
                                        onClick={handleLogout}
                                        className="block w-full text-left rounded-md px-3 py-2.5 text-base text-charcoal/70"
                                    >
                                        Déconnexion
                                    </button>
                                </>
                            ) : (
                                <>
                                    <Link
                                        to="/connexion"
                                        className="block rounded-md px-3 py-2.5 text-base text-charcoal"
                                    >
                                        Connexion
                                    </Link>
                                    <Link
                                        to="/inscription"
                                        className="block rounded-md px-3 py-2.5 text-base text-charcoal"
                                    >
                                        Créer un compte
                                    </Link>
                                </>
                            )}
                        </div>

                        {!isAdmin && (
                            <Link
                                to={CTA.to}
                                className="inline-flex items-center justify-center gap-2 rounded-full bg-ink px-5 py-3 text-base font-medium text-cream mt-6 w-full transition-all active:scale-95"
                            >
                                {CTA.label}
                                <ArrowRight size={16} />
                            </Link>
                        )}
                    </nav>
                </div>
            )}

            {/* Floating bottom-right CTA quand on a scrollé (visiteurs / users uniquement)
                Position au-DESSUS de la bulle chatbot Emma (qui occupe bottom-6 right-6) */}
            {!isAdmin && (
                <div
                    className={`fixed bottom-24 right-6 z-50 transition-all duration-500 ease-out ${
                        compactMode
                            ? "translate-y-0 opacity-100 scale-100"
                            : "translate-y-8 opacity-0 scale-95 pointer-events-none"
                    }`}
                >
                    <Link
                        to={CTA.to}
                        className="inline-flex items-center justify-center gap-2 rounded-full bg-ink px-6 py-3.5 text-sm font-medium text-cream shadow-xl shadow-ink/20 ring-1 ring-white/10 transition-all hover:-translate-y-1 hover:bg-ink-deep hover:shadow-2xl"
                    >
                        {CTA.label}
                        <ArrowRight size={16} />
                    </Link>
                </div>
            )}

            {/* Modal Être rappelé */}
            <CallbackPopup open={callbackOpen} onOpenChange={setCallbackOpen} />
        </>
    );
}
